import { useState, useCallback } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { formatBCH, formatFiat } from '@/lib/bch';
import { Transaction, markTransactionRefunded, unmarkTransactionRefunded, loadTransactions, Shift, openShift, closeShift, getActiveShift, loadShifts } from '@/lib/posStorage';
import { FREE_HISTORY_LIMIT } from '@/lib/pro';
import { cn } from '@/lib/utils';

function formatTime(timestamp: number): string {
  const d = new Date(timestamp);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms: number): string {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function exportCSV(transactions: Transaction[]) {
  const header = [
    'Date', 'Note', 'Subtotal', 'Discount Label', 'Discount Amount',
    'Tax Name', 'Tax Rate (%)', 'Tax Amount', 'Tip', 'Total',
    'Currency', 'BCH Amount', 'Transaction ID', 'Address', 'Refunded',
  ];
  const rows = transactions.map((tx) => [
    new Date(tx.timestamp).toISOString(),
    tx.note ?? '',
    (tx.subtotal ?? tx.fiatAmount).toFixed(2),
    tx.discountLabel ?? '',
    (tx.discountAmount ?? 0).toFixed(2),
    tx.taxName ?? 'Tax',
    (tx.taxRate ?? 0).toString(),
    (tx.taxAmount ?? 0).toFixed(2),
    (tx.tipAmount ?? 0).toFixed(2),
    tx.fiatAmount.toFixed(2),
    tx.currency,
    tx.bchAmount.toFixed(8),
    tx.txid,
    tx.address,
    tx.refunded ? 'Yes' : 'No',
  ]);
  const csv = [header, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bch-pos-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Tax Summary Panel ────────────────────────────────────────────────────────

type TaxPeriod = 'month' | 'quarter' | 'year' | 'all';

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1).getTime(); }
function startOfQuarter(d: Date) { const q = Math.floor(d.getMonth() / 3); return new Date(d.getFullYear(), q * 3, 1).getTime(); }
function startOfYear(d: Date) { return new Date(d.getFullYear(), 0, 1).getTime(); }

function periodLabel(period: TaxPeriod): string {
  const now = new Date();
  if (period === 'month') return now.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  if (period === 'quarter') { const q = Math.floor(now.getMonth() / 3) + 1; return `Q${q} ${now.getFullYear()}`; }
  if (period === 'year') return `${now.getFullYear()}`;
  return 'All Time';
}

function filterByPeriod(transactions: Transaction[], period: TaxPeriod): Transaction[] {
  if (period === 'all') return transactions;
  const now = new Date();
  const start = period === 'month' ? startOfMonth(now) : period === 'quarter' ? startOfQuarter(now) : startOfYear(now);
  return transactions.filter((tx) => tx.timestamp >= start);
}

function TaxSummary({ transactions, currency }: { transactions: Transaction[]; currency: string }) {
  const [period, setPeriod] = useState<TaxPeriod>('month');
  const [copied, setCopied] = useState(false);
  const hasTax = transactions.some((tx) => (tx.taxRate ?? 0) > 0);
  if (!hasTax) return null;
  const filtered = filterByPeriod(transactions.filter((tx) => !tx.refunded), period);
  const taxTotal = filtered.reduce((sum, tx) => sum + (tx.taxAmount ?? 0), 0);
  const revenueTotal = filtered.reduce((sum, tx) => sum + (tx.subtotal ?? tx.fiatAmount), 0);
  const txCount = filtered.filter((tx) => (tx.taxAmount ?? 0) > 0).length;
  const taxName = transactions.find((tx) => tx.taxName)?.taxName ?? 'Tax';
  const PERIODS: { id: TaxPeriod; label: string }[] = [{ id: 'month', label: 'Month' }, { id: 'quarter', label: 'Quarter' }, { id: 'year', label: 'Year' }, { id: 'all', label: 'All' }];

  return (
    <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-2"><span className="text-base">🏛️</span><p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">{taxName} Summary</p></div>
        <div className="flex bg-zinc-100 dark:bg-zinc-700 rounded-lg p-0.5 gap-0.5">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${period === p.id ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-zinc-50 shadow-sm' : 'text-zinc-400'}`}>{p.label}</button>
          ))}
        </div>
      </div>
      <div className="px-4 py-4 space-y-4">
        <p className="text-xs text-zinc-400 font-medium">{periodLabel(period)} · {txCount} taxed sale{txCount !== 1 ? 's' : ''}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-xl p-3">
            <p className="text-xs text-zinc-400 mb-1">Revenue (ex-{taxName})</p>
            <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{formatFiat(revenueTotal, currency)}</p>
          </div>
          <div className="bg-[#0AC18E]/8 dark:bg-[#0AC18E]/15 rounded-xl p-3 border border-[#0AC18E]/20">
            <p className="text-xs text-[#0AC18E] font-semibold mb-1">{taxName} Collected</p>
            <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{formatFiat(taxTotal, currency)}</p>
          </div>
        </div>
        {taxTotal > 0 && (
          <div className="bg-zinc-50 dark:bg-zinc-900 rounded-xl p-4 border border-zinc-200 dark:border-zinc-700">
            <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-300 mb-1">💡 Transfer reminder</p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed mb-3">
              You collected <span className="font-bold text-zinc-700 dark:text-zinc-200">{formatFiat(taxTotal, currency)}</span> in {taxName} {period === 'all' ? 'total' : `this ${period}`}.
            </p>
            <button onClick={async () => { try { await navigator.clipboard.writeText(taxTotal.toFixed(2)); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { } }} className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg transition-all ${copied ? 'bg-[#0AC18E]/15 text-[#0AC18E] border border-[#0AC18E]/30' : 'bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-600'}`}>
              {copied ? '✓ Copied!' : `📋 Copy ${formatFiat(taxTotal, currency)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shift Panel ──────────────────────────────────────────────────────────────

function ShiftPanel({ transactions, currency, onShiftChange }: { transactions: Transaction[]; currency: string; onShiftChange: () => void }) {
  const [activeShift, setActiveShift] = useState<Shift | null>(() => getActiveShift());
  const [showReport, setShowReport] = useState(false);
  const [staffNote, setStaffNote] = useState('');
  const [reportShift, setReportShift] = useState<Shift | null>(null);
  const [pastShifts, setPastShifts] = useState<Shift[]>(() => loadShifts().filter((s) => s.closedAt));

  const handleOpen = () => {
    const shift = openShift(staffNote.trim() || undefined);
    setActiveShift(shift);
    setStaffNote('');
    onShiftChange();
  };

  const handleClose = () => {
    if (!activeShift) return;
    closeShift(activeShift.id);
    const closed = { ...activeShift, closedAt: Date.now() };
    setActiveShift(null);
    setPastShifts(loadShifts().filter((s) => s.closedAt));
    setReportShift(closed);
    setShowReport(true);
    onShiftChange();
  };

  // Transactions within a shift window
  const shiftTxs = useCallback((shift: Shift) => {
    const end = shift.closedAt ?? Date.now();
    return transactions.filter((tx) => tx.timestamp >= shift.openedAt && tx.timestamp <= end && !tx.refunded);
  }, [transactions]);

  const currentTxs = activeShift ? shiftTxs(activeShift) : [];
  const currentFiat = currentTxs.reduce((s, tx) => s + tx.fiatAmount, 0);
  const currentBCH = currentTxs.reduce((s, tx) => s + tx.bchAmount, 0);

  return (
    <>
      {/* Active shift banner */}
      {activeShift ? (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 overflow-hidden">
          <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 border-b border-[#0AC18E]/20 px-4 py-2.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#0AC18E] animate-pulse" />
            <p className="text-xs font-bold text-[#0AC18E] uppercase tracking-wider flex-1">Shift Open</p>
            <p className="text-xs text-zinc-400">{formatTime(activeShift.openedAt)}</p>
          </div>
          <div className="px-4 py-3 flex items-center gap-4">
            <div className="flex-1 space-y-0.5">
              {activeShift.note && <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">{activeShift.note}</p>}
              <div className="flex gap-4 text-sm">
                <span className="font-bold text-zinc-900 dark:text-zinc-50">{formatFiat(currentFiat, currency)}</span>
                <span className="font-mono text-[#0AC18E]">{formatBCH(currentBCH)} BCH</span>
                <span className="text-zinc-400">{currentTxs.length} sale{currentTxs.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="shrink-0 px-4 py-2 rounded-xl bg-zinc-900 dark:bg-zinc-700 text-white text-xs font-bold hover:bg-zinc-700 dark:hover:bg-zinc-600 transition-colors"
            >
              Close Day →
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Start a new shift</p>
              <p className="text-xs text-zinc-400 mt-0.5">Track sales for this session and get a Z-report when you close.</p>
            </div>
            <button
              onClick={handleOpen}
              className="shrink-0 px-4 py-2 rounded-xl bg-[#0AC18E] text-white text-xs font-bold shadow-sm shadow-[#0AC18E]/30 hover:bg-[#09AF81] transition-colors"
            >
              Open Day
            </button>
          </div>
          {/* Optional staff name */}
          <input
            type="text"
            value={staffNote}
            onChange={(e) => setStaffNote(e.target.value)}
            placeholder="Staff name (optional)"
            className="mt-3 w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>
      )}

      {/* Past shifts */}
      {pastShifts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider px-1">Past Shifts</p>
          {pastShifts.slice(0, 5).map((shift) => {
            const txs = shiftTxs(shift);
            const fiat = txs.reduce((s, tx) => s + tx.fiatAmount, 0);
            const bch = txs.reduce((s, tx) => s + tx.bchAmount, 0);
            const duration = shift.closedAt ? shift.closedAt - shift.openedAt : 0;
            return (
              <button
                key={shift.id}
                onClick={() => { setReportShift(shift); setShowReport(true); }}
                className="w-full bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 px-4 py-3 flex items-center gap-3 text-left hover:border-[#0AC18E]/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{formatTime(shift.openedAt)}</p>
                    {shift.note && <span className="text-xs text-zinc-400 truncate">· {shift.note}</span>}
                  </div>
                  <p className="text-xs text-zinc-400 mt-0.5">{txs.length} sales · {formatDuration(duration)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">{formatFiat(fiat, currency)}</p>
                  <p className="text-xs font-mono text-[#0AC18E]">{formatBCH(bch)}</p>
                </div>
                <span className="text-zinc-400 text-xs ml-1">→</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Z-Report modal */}
      {showReport && reportShift && (
        <ZReport
          shift={reportShift}
          transactions={shiftTxs(reportShift)}
          currency={currency}
          onClose={() => setShowReport(false)}
        />
      )}
    </>
  );
}

// ─── Z-Report modal ───────────────────────────────────────────────────────────

function ZReport({ shift, transactions, currency, onClose }: { shift: Shift; transactions: Transaction[]; currency: string; onClose: () => void }) {
  const totalFiat = transactions.reduce((s, tx) => s + tx.fiatAmount, 0);
  const totalBCH = transactions.reduce((s, tx) => s + tx.bchAmount, 0);
  const totalTax = transactions.reduce((s, tx) => s + (tx.taxAmount ?? 0), 0);
  const totalTips = transactions.reduce((s, tx) => s + (tx.tipAmount ?? 0), 0);
  const totalDiscount = transactions.reduce((s, tx) => s + (tx.discountAmount ?? 0), 0);
  const duration = shift.closedAt ? shift.closedAt - shift.openedAt : Date.now() - shift.openedAt;
  const taxName = transactions.find((tx) => tx.taxName)?.taxName ?? 'Tax';
  const isOpen = !shift.closedAt;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl overflow-hidden max-h-[90dvh] flex flex-col">
        {/* Header */}
        <div className="bg-zinc-900 px-5 py-4 flex items-center gap-3 shrink-0">
          <div>
            <p className="text-white font-black text-base">
              {isOpen ? 'Current Shift' : 'Z-Report'}
            </p>
            <p className="text-zinc-400 text-xs">
              {formatTime(shift.openedAt)}
              {shift.closedAt ? ` → ${formatTime(shift.closedAt)}` : ' (open)'}
              {' · '}{formatDuration(duration)}
              {shift.note ? ` · ${shift.note}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="ml-auto text-zinc-500 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Main totals */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-2xl p-4 border border-[#0AC18E]/20 col-span-2">
              <p className="text-xs text-[#0AC18E] font-bold uppercase tracking-wider mb-1">Total Revenue</p>
              <p className="text-3xl font-black text-zinc-900 dark:text-zinc-50">{formatFiat(totalFiat, currency)}</p>
              <p className="text-base font-mono text-[#0AC18E] mt-0.5">{formatBCH(totalBCH)} BCH</p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-800 rounded-xl p-3 border border-zinc-200 dark:border-zinc-700">
              <p className="text-xs text-zinc-400 mb-1">Transactions</p>
              <p className="text-2xl font-black text-zinc-800 dark:text-zinc-100">{transactions.length}</p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-800 rounded-xl p-3 border border-zinc-200 dark:border-zinc-700">
              <p className="text-xs text-zinc-400 mb-1">Avg Sale</p>
              <p className="text-2xl font-black text-zinc-800 dark:text-zinc-100">
                {transactions.length > 0 ? formatFiat(totalFiat / transactions.length, currency) : '—'}
              </p>
            </div>
          </div>

          {/* Breakdown */}
          <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 divide-y divide-zinc-100 dark:divide-zinc-700 overflow-hidden">
            {[
              { label: 'Gross Sales', value: formatFiat(totalFiat, currency) },
              ...(totalDiscount > 0 ? [{ label: 'Discounts Given', value: `−${formatFiat(totalDiscount, currency)}`, red: true }] : []),
              ...(totalTax > 0 ? [{ label: `${taxName} Collected`, value: formatFiat(totalTax, currency) }] : []),
              ...(totalTips > 0 ? [{ label: 'Tips', value: formatFiat(totalTips, currency), green: true }] : []),
            ].map(({ label, value, red, green }) => (
              <div key={label} className="flex justify-between items-center px-4 py-3">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">{label}</span>
                <span className={cn('text-sm font-bold', red ? 'text-red-500' : green ? 'text-[#0AC18E]' : 'text-zinc-800 dark:text-zinc-100')}>{value}</span>
              </div>
            ))}
          </div>

          {transactions.length === 0 && (
            <p className="text-center text-sm text-zinc-400 py-4">No transactions in this shift.</p>
          )}
        </div>

        <div className="px-5 pb-6 pt-2 shrink-0">
          <button onClick={onClose} className="w-full py-4 rounded-2xl bg-[#0AC18E] text-white font-bold text-base shadow-lg shadow-[#0AC18E]/30">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Transaction card ─────────────────────────────────────────────────────────

function TransactionCard({ tx, onRefundChange }: { tx: Transaction; onRefundChange: () => void }) {
  const isOfflineTx = tx.txid.startsWith('offline-');
  const explorerUrl = isOfflineTx ? null : `https://bchexplorer.cash/tx/${tx.txid}`;
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundNote, setRefundNote] = useState('');

  const handleMarkRefunded = () => {
    markTransactionRefunded(tx.id, refundNote.trim() || undefined);
    setShowRefundDialog(false);
    setRefundNote('');
    onRefundChange();
  };

  const handleUnmarkRefunded = () => {
    unmarkTransactionRefunded(tx.id);
    onRefundChange();
  };

  return (
    <>
      <div className={cn(
        'bg-white dark:bg-zinc-800 rounded-2xl p-4 border shadow-sm transition-opacity',
        tx.refunded
          ? 'border-red-200 dark:border-red-900/40 opacity-60'
          : 'border-zinc-100 dark:border-zinc-700'
      )}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span className={cn('text-xl font-bold', tx.refunded ? 'line-through text-zinc-400' : 'text-zinc-900 dark:text-zinc-50')}>
              {formatFiat(tx.fiatAmount, tx.currency)}
            </span>
            {tx.refunded && (
              <span className="text-xs font-bold text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full border border-red-200 dark:border-red-800">
                Refunded
              </span>
            )}
          </div>
          <span className={cn('text-sm font-mono font-medium', tx.refunded ? 'text-zinc-400 line-through' : 'text-[#0AC18E]')}>
            {tx.asset === 'PUSD' && tx.tokenAmount != null
              ? `${tx.tokenAmount.toFixed(2)} ${tx.tokenSymbol ?? 'PUSD'}`
              : `${formatBCH(tx.bchAmount)} BCH`}
          </span>
        </div>

        {tx.note && <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-1 truncate">📝 {tx.note}</p>}
        {tx.refunded && tx.refundNote && <p className="text-xs text-red-400 mb-1 truncate">↩ {tx.refundNote}</p>}

        {((tx.discountAmount ?? 0) > 0 || (tx.taxAmount ?? 0) > 0 || (tx.tipAmount ?? 0) > 0) && (
          <div className="flex flex-wrap gap-2 mb-1.5">
            {(tx.discountAmount ?? 0) > 0 && <p className="text-xs text-red-400 font-medium">−{formatFiat(tx.discountAmount ?? 0, tx.currency)} off</p>}
            {(tx.taxAmount ?? 0) > 0 && <p className="text-xs text-zinc-400">{tx.taxName ?? 'Tax'} {formatFiat(tx.taxAmount ?? 0, tx.currency)}</p>}
            {(tx.tipAmount ?? 0) > 0 && <p className="text-xs text-[#0AC18E] font-medium">Tip {formatFiat(tx.tipAmount ?? 0, tx.currency)}</p>}
          </div>
        )}

        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-zinc-400">{formatTime(tx.timestamp)}</span>
          <div className="flex items-center gap-3">
            {explorerUrl ? (
              <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-[#0AC18E] hover:underline" title={tx.txid}>
                {tx.txid.slice(0, 8)}…{tx.txid.slice(-6)}
              </a>
            ) : (
              <span className="text-xs font-medium text-amber-500" title="Confirmed offline — no blockchain transaction ID">
                offline
              </span>
            )}
            {tx.refunded ? (
              <button onClick={handleUnmarkRefunded} className="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors">
                Undo
              </button>
            ) : (
              <button onClick={() => setShowRefundDialog(true)} className="text-xs text-red-400 hover:text-red-600 transition-colors font-medium">
                Refund
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Refund confirmation dialog */}
      {showRefundDialog && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl p-5 pb-safe space-y-4">
            <div className="w-10 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full mx-auto" />
            <div>
              <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-50">Mark as Refunded?</h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                This marks the sale as refunded in your records. It does <span className="font-semibold text-zinc-600 dark:text-zinc-300">not</span> move any BCH — you'll need to send the refund manually from your wallet.
              </p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-800 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-700">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-zinc-500">Amount</span>
                <span className="font-bold text-zinc-800 dark:text-zinc-100">{formatFiat(tx.fiatAmount, tx.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">BCH</span>
                <span className="font-mono text-[#0AC18E]">{formatBCH(tx.bchAmount)} BCH</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Reason (optional)</label>
              <input
                type="text"
                value={refundNote}
                onChange={(e) => setRefundNote(e.target.value)}
                placeholder="e.g. Customer request, wrong item…"
                className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowRefundDialog(false)} className="flex-1 py-4 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700">
                Cancel
              </button>
              <button onClick={handleMarkRefunded} className="flex-[2] py-4 rounded-2xl text-sm font-bold bg-red-500 text-white shadow-lg shadow-red-500/30 hover:bg-red-600 active:scale-98 transition-all">
                Mark as Refunded
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function HistoryView() {
  const { transactions: ctxTransactions, settings, isPro, setView } = usePOS();
  const [transactions, setTransactions] = useState(ctxTransactions);

  // Reload transactions from storage after refund changes
  const handleRefundChange = useCallback(() => {
    setTransactions(loadTransactions());
  }, []);

  const visibleTxs = isPro ? transactions : transactions.slice(0, FREE_HISTORY_LIMIT);
  const hiddenCount = transactions.length - visibleTxs.length;

  // Exclude refunded from totals
  const activeTxs = visibleTxs.filter((tx) => !tx.refunded);
  const refundedCount = visibleTxs.filter((tx) => tx.refunded).length;
  const totalFiat = activeTxs.reduce((sum, tx) => sum + tx.fiatAmount, 0);
  const totalBCH = activeTxs.reduce((sum, tx) => sum + tx.bchAmount, 0);

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Transaction History</h2>
          <p className="text-sm text-zinc-400">
            {isPro ? `${transactions.length} transactions` : `${visibleTxs.length} of ${transactions.length} shown`}
            {refundedCount > 0 && <span className="text-red-400"> · {refundedCount} refunded</span>}
          </p>
        </div>
        {isPro && transactions.length > 0 && (
          <button onClick={() => exportCSV(transactions)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 text-[#0AC18E] text-xs font-bold border border-[#0AC18E]/20 hover:bg-[#0AC18E]/20 transition-colors">
            📊 Export CSV
          </button>
        )}
      </div>

      {/* Shift panel — available to all users */}
      <ShiftPanel transactions={transactions} currency={settings.currency} onShiftChange={handleRefundChange} />

      {/* Revenue totals */}
      {activeTxs.length > 0 && (
        <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 rounded-2xl p-4 border border-[#0AC18E]/20">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">
                {isPro ? 'Total Revenue' : 'Visible Revenue'}
              </p>
              <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-50">{formatFiat(totalFiat, settings.currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">Total BCH</p>
              <p className="text-lg font-mono font-bold text-[#0AC18E]">{formatBCH(totalBCH)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tax summary */}
      {isPro && <TaxSummary transactions={transactions} currency={settings.currency} />}

      {/* Transaction list */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-4">
        {transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-zinc-200 dark:border-zinc-700 rounded-3xl">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-zinc-500 dark:text-zinc-400 font-medium">No transactions yet</p>
            <p className="text-sm text-zinc-400 dark:text-zinc-600 mt-1">Completed payments will appear here</p>
          </div>
        ) : (
          <>
            {visibleTxs.map((tx) => (
              <TransactionCard key={tx.id} tx={tx} onRefundChange={handleRefundChange} />
            ))}
            {!isPro && hiddenCount > 0 && (
              <button onClick={() => setView('upgrade')} className="w-full bg-[#0AC18E]/5 dark:bg-[#0AC18E]/10 border border-dashed border-[#0AC18E]/30 dark:border-[#0AC18E]/40 rounded-2xl p-5 text-center">
                <p className="text-2xl mb-2">🔒</p>
                <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200 mb-1">{hiddenCount} older transaction{hiddenCount !== 1 ? 's' : ''} hidden</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">Upgrade to Pro to see your full history and export CSV</p>
                <span className="text-xs font-bold text-white bg-[#0AC18E] px-4 py-1.5 rounded-full">Upgrade to Pro →</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
